import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";

import { useQuery } from "@tanstack/react-query";

import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import { useOktaHelpers } from "shared/hooks/useOktaHelpers";

export function useFetchLayoutUserLocations(
  isAuthenticated,
  email,
  currentOrganization,
  currentOrganizationType,
  currentRoute
) {
  const { oktaAuth } = useOktaAuth();

  const dataKey = "useFetchLayoutUserLocationsAccessKey";

  const { invalidTokenOnErrorHandler } = useOktaHelpers();

  async function getAllLocations() {
    const accessToken = oktaAuth.getAccessToken();

    let allLocationsParameters = {
      email: email,
      selectedItem: currentOrganization,
      organizationType: currentOrganizationType,
    };

    // alert("calling getAllLocations");

    let header = {
      Authorization: `Bearer ${accessToken}`,
      RequestSrc: currentRoute,
    };

    var result = await axios
      .get("api/UserProgram/GetUserLocationsDetails", {
        params: allLocationsParameters,
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        //will check if the token is valid and redirect to login page if not
        invalidTokenOnErrorHandler(error);

        throw new Error(error);
      });

    return result;
  }

  async function getRecLocations() {
    const accessToken = oktaAuth.getAccessToken();

    let recParameters = {
      email: email,
      selectedItem: currentOrganization,
      organizationType: currentOrganizationType,
      programSubscriptions: "Rec2.0",
    };

    let header = {
      Authorization: `Bearer ${accessToken}`,
      RequestSrc: currentRoute,
    };

    var result = await axios
      .get("api/UserProgram/GetUserLocationsDetails", {
        params: recParameters,
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error(error);
      });

    return result;
  }

  async function getMetricLocations() {
    const accessToken = oktaAuth.getAccessToken();

    let metricParameters = {
      email: email,
      selectedItem: currentOrganization,
      organizationType: currentOrganizationType,
      programSubscriptions: "Metric2.0,Metric2.0 Premium",
    };

    let header = {
      Authorization: `Bearer ${accessToken}`,
      RequestSrc: currentRoute,
    };

    var result = await axios
      .get("api/UserProgram/GetUserLocationsDetails", {
        params: metricParameters,
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error(error);
      });

    return result;
  }

  function IsEnabled() {
    if (!isAuthenticated) return false;

    if (IsNullOrEmpty(email)) return false;

    if (IsNullOrEmpty(currentOrganization)) return false;

    if (IsNullOrEmpty(currentOrganizationType)) return false;

    return true;
  }

  let { data: allLocationsData, isLoading: isLoadingAllLocations } = useQuery({
    queryKey: [
      dataKey,
      [
        "alllocations",
        email,
        currentOrganization,
        currentOrganizationType,
        currentRoute,
      ],
    ],
    queryFn: async () => await getAllLocations(),
    enabled: IsEnabled,
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes, in case location data changes
  });

  let { data: recLocationsData, isLoading: isLoadingRecLocations } = useQuery({
    queryKey: [
      dataKey,
      [
        "reclocations",
        email,
        currentOrganization,
        currentOrganizationType,
        currentRoute,
      ],
    ],
    queryFn: async () => await getRecLocations(),
    enabled: IsEnabled,
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes, in case location data changes
  });

  let { data: metricLocationsData, isLoading: isLoadingMetricLocations } =
    useQuery({
      queryKey: [
        dataKey,
        [
          "metriclocations",
          email,
          currentOrganization,
          currentOrganizationType,
          currentRoute,
        ],
      ],
      queryFn: async () => await getMetricLocations(),
      enabled: IsEnabled,
      refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes, in case location data changes
    });

  return {
    allLocationsData,
    recLocationsData,
    metricLocationsData,
    isLoading:
      isLoadingAllLocations ||
      isLoadingRecLocations ||
      isLoadingMetricLocations,
  };
}
